import {useRecoilState} from "recoil";
import recoil from "../../../../stores/recoil";
import {useCallback, useEffect, useRef, useState, useMemo} from "react";
import {useAtom} from "jotai/index";
import {uploadedInfo} from "../../../../stores/jotai/jotai";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {S3URLFindRegex} from "../../../../constants/RegexConstants";
import {BLOG_SAVE_TYPE} from "../../../../constants/constants";
import {getDocument, saveDocument} from "../../../../endpoints/blog-endpoints";
import useAuthEP from "../../../../utils/useAuthEP";
import { Editor } from "@tiptap/react";
import {LocaleCode, SUPPORTED_LOCALES} from "../../../../types/i18n/i18nTypes";
import {getTranslationDetail, updateTranslation} from "../../../../endpoints/i18n-endpoints";
import {toaster} from "../../../../common/elements/toaster";

export interface useSaveBlogPostProps {

};

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

// 3분
const AUTO_SAVE_INTERVAL_MS = 3 * 60 * 1000;

function useSaveBlogPost(props : useSaveBlogPostProps) {
  const [errMsg, setErrMsg] = useRecoilState(recoil.errMsg);
  const editorRef = useRef<Editor | null>(null);
  const [document, setDocument] = useState<DocumentDTO>()
  const [uploadedList, setUploadedList] = useAtom<any[]>(uploadedInfo);
  const {id, locale: localeParam} = useParams<{id: string; locale?: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const authEP = useAuthEP();
  // 저장 모달 노출 여부는 페이지 로컬 상태로 관리한다.
  // (전역 atom 으로 두면 저장 후 navigate 시 true 가 남아, 이후 Edit 진입 시
  //  모달이 처음부터 떠버리는 버그가 있었음 → 마운트마다 닫힌 상태로 시작)
  const [openBlogPostingModal, setOpenBlogPostingModal] = useState<boolean>(false);
  const [selectedFolder, setSelectedFolder] = useState<string | undefined>(undefined);
  const [folders, setFolders] = useState<any>({});
  const [disclose, setDisclose] = useState<boolean>(true);

  // 자동저장 상태
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  // setInterval 콜백이 stale closure 에 빠지지 않게 ref 로 들고 다님
  const lastSavedSnapshotRef = useRef<string>("");
  const isAutoSavingRef = useRef<boolean>(false);
  const navigatedAwayRef = useRef<boolean>(false);

  // 번역 편집 여부 판별
  const locale = localeParam && SUPPORTED_LOCALES.includes(localeParam as LocaleCode) ? localeParam as LocaleCode : null;
  const isTranslationEdit = useMemo(() => {
    return !!locale && locale !== 'ko';
  }, [locale]);
  
  // 컴포넌트 외부 또는 내부에 추가
  const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.quicktime'];
  
  const generateThumbnailUrl = (originalUrl: string): string => {
    // 버킷 변경: haries-img -> haries-thumbnail
    let thumbnailUrl = originalUrl.replace('haries-img', 'haries-thumbnail');
    
    // 영상 파일인 경우 확장자를 .jpg로 변경
    const lowerUrl = thumbnailUrl.toLowerCase();
    for (const ext of VIDEO_EXTENSIONS) {
      if (lowerUrl.endsWith(ext)) {
        const extIndex = thumbnailUrl.toLowerCase().lastIndexOf(ext);
        thumbnailUrl = thumbnailUrl.substring(0, extIndex) + '.jpg';
        break;
      }
    }
    
    return thumbnailUrl;
  };
  
  // 글 저장 - Tiptap 버전
  const handleEdit = useCallback(async (saveOrDraft: string) => {
    if (editorRef?.current){
      // Tiptap 에디터 인스턴스
      const editorInstance = editorRef.current;

      // Tiptap에서 HTML 콘텐츠 가져오기
      const contents = editorInstance.getHTML();

      // 번역 편집인 경우: 번역 API로 저장
      if (isTranslationEdit && locale) {
        try {
          const saveRes = await authEP({
            func: updateTranslation,
            params: { postId: id, locale },
            reqBody: {
              title: document?.title || '',
              content: contents,
            }
          });

          if (saveRes.status === 200) {
            navigate(`/blog/view/${locale}/${id}`);
          } else {
            throw new Error("저장 실패");
          }
        } catch (e) {
          console.error("번역 저장 에러:", e);
          setErrMsg({
            status: "error",
            msg: "번역 저장에 실패했습니다.",
          });
        }
        return;
      }

      // 원본 편집인 경우: 기존 로직
      const regex = S3URLFindRegex;
      let settingThumbnailUrl : string = "";

      const match = contents.match(regex);

      if (match && match[0]) {
        const originalUrl = match[0];
        settingThumbnailUrl = generateThumbnailUrl(originalUrl);
      }

      let isDraft: boolean = false;
      if (saveOrDraft === BLOG_SAVE_TYPE.SAVE) {
        isDraft = false;
      } else if (saveOrDraft === BLOG_SAVE_TYPE.DRAFT) {
        isDraft = true;
      }

      const request: DocumentDTO = {
        ...document,
        contents: contents,
        thumbnailImgUrl: settingThumbnailUrl || "",
        draft: isDraft,
        disclose : disclose,
        folderId : selectedFolder
      }

      try {
        const saveRes = await authEP({
          func: saveDocument,
          reqBody: request
        })

        if (saveRes.status === 200) {
          navigatedAwayRef.current = true;
          navigate(`/blog/view/${locale || 'ko'}/${id}`)
        } else {
          throw new Error("저장 실패");
        }
      } catch (e) {
        console.error("저장 에러:", e);
        setErrMsg({
          status: "error",
          msg: "저장에 실패했습니다.",
        })
      }
    } else {
      setErrMsg({
        status: "error",
        msg: "에디터가 초기화되지 않았습니다.",
      })
    }
  }, [document, uploadedList, navigate, setErrMsg, selectedFolder, disclose, id, authEP, isTranslationEdit, locale]);

  // 자동저장: 3분마다 draft 로 임시저장 (수동 Save 와 달리 네비게이션 없음)
  // - 번역 편집 모드는 draft 개념이 없어 제외
  // - 직전 저장 이후 컨텐츠 변경이 없으면 호출 자체를 skip (불필요한 API 호출 방지)
  // - 발행/임시 상태(draft flag)는 그대로 보존 — 자동저장이 발행글을 다시 draft 로 되돌리면 안 됨
  const autoSaveDraft = useCallback(async () => {
    if (navigatedAwayRef.current) return;
    if (isAutoSavingRef.current) return;
    if (isTranslationEdit) return;
    if (!editorRef.current || !document || !id) return;

    const contents = editorRef.current.getHTML();
    const title = document.title ?? "";
    const snapshot = `${title} ${contents}`;
    if (snapshot === lastSavedSnapshotRef.current) return;

    // 빈 문서는 자동저장 스킵 (사용자가 아무것도 안 썼을 때 의미 없는 저장 방지)
    const isEmpty = !title.trim() && (!contents || contents === "<p></p>");
    if (isEmpty) return;

    isAutoSavingRef.current = true;
    setAutoSaveStatus("saving");

    const regex = S3URLFindRegex;
    const match = contents.match(regex);
    const settingThumbnailUrl = match && match[0] ? generateThumbnailUrl(match[0]) : "";

    const request: DocumentDTO = {
      ...document,
      contents,
      thumbnailImgUrl: settingThumbnailUrl || document.thumbnailImgUrl || "",
      // 현재 문서의 draft 상태를 그대로 유지. 신규 작성(undefined)은 draft 로 간주.
      draft: document.draft ?? true,
      disclose,
      folderId: selectedFolder,
    };

    try {
      const res = await authEP({
        func: saveDocument,
        reqBody: request,
      });
      if (navigatedAwayRef.current) return;
      if (res.status === 200) {
        lastSavedSnapshotRef.current = snapshot;
        setLastSavedAt(new Date());
        setAutoSaveStatus("saved");
        toaster.create({
          title: "Saved",
          type: "success",
          duration: 2000,
        });
      } else {
        setAutoSaveStatus("error");
      }
    } catch (e) {
      console.error("자동저장 실패:", e);
      if (!navigatedAwayRef.current) setAutoSaveStatus("error");
    } finally {
      isAutoSavingRef.current = false;
    }
  }, [document, disclose, selectedFolder, id, authEP, isTranslationEdit]);

  // autoSaveDraft 는 document 가 바뀔 때마다 재생성되므로, 타이핑마다 setInterval 이
  // 재등록되는 걸 막기 위해 ref 로 최신 콜백을 들고 다닌다. 인터벌 자체는 마운트 시 1회만 등록.
  const autoSaveDraftRef = useRef(autoSaveDraft);
  useEffect(() => {
    autoSaveDraftRef.current = autoSaveDraft;
  }, [autoSaveDraft]);

  useEffect(() => {
    if (isTranslationEdit) return;
    const timer = setInterval(() => {
      autoSaveDraftRef.current?.();
    }, AUTO_SAVE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isTranslationEdit]);
  
  const handleSaveModalOpen = () => {
    setOpenBlogPostingModal(true);
  }
  
  const getDocumentData = useCallback(async () => {
    if (!id) return;

    try {
      // 번역 편집인 경우: 번역 데이터 로드
      if (isTranslationEdit && locale) {
        const res = await authEP({
          func: getTranslationDetail,
          params: { postId: id, locale },
        });
        if (res.data) {
          // TranslationDetailDTO → DocumentDTO 형태로 매핑
          setDocument({
            id: id,
            title: res.data.title,
            contents: res.data.content,
          });
        }
        return;
      }

      // 원본 편집인 경우: 기존 로직
      const res = await getDocument({
        params : {
          id : id
        }
      });

      if (res.data) {
        setDocument(res.data)
        setSelectedFolder(res.data.folderId);
        setDisclose(res.data.disclose ?? true);
      }
    } catch (e) {
      console.error("문서 불러오기 실패:", e);
      setErrMsg({
        status: "error",
        msg: "문서를 불러오는데 실패했습니다.",
      })
    }
  }, [id, setErrMsg, isTranslationEdit, locale, authEP]);
  
  const modalClose = () => {
    // 닫기는 항상 false (토글 금지 — 닫기 동작이 모달을 다시 여는 일이 없도록)
    setOpenBlogPostingModal(false)
  }
  
  const goBack = () => {
    // 수정(Edit) 화면에서 취소 시: 해당 글의 뷰어로 복귀한다.
    // 새 글 작성(Create) 화면에서는 빈 draft 뷰어로 갈 이유가 없으므로 블로그 홈으로 이동.
    if (location.pathname.includes("/edit/") && id) {
      navigate(`/blog/view/${locale || 'ko'}/${id}`)
    } else {
      navigate("/blog/home")
    }
  }
  
  useEffect(() => {
    getDocumentData();
  }, [id])
  
  return {
    editorRef,
    document,
    setDocument,
    handleEdit,
    modalClose,
    openBlogPostingModal,
    handleSaveModalOpen,
    folders,
    setFolders,
    selectedFolder,
    setSelectedFolder,
    disclose,
    setDisclose,
    goBack,
    isTranslationEdit,
    autoSaveStatus,
    lastSavedAt,
  }
}

export default useSaveBlogPost;